/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import {
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { usePagination } from "app/hooks/usePagination";
import { Table } from "app/components";
import Div from "@jumbo/shared/Div";
import { useDispatch, useSelector } from "react-redux";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { getLogsList } from "app/redux/slice/logsSlice";
import { getUserList } from "app/redux/slice/userSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getVendorList } from "app/redux/slice/vendorSlice";

const modules = [
  { id: "user", label: "User" },
  { id: "role", label: "Role" },
  { id: "rolePermission", label: "Role Permission" },
  { id: "permission", label: "Permission" },
  { id: "vendor", label: "Vendor" },
  { id: "size", label: "Size" },
  { id: "category", label: "Category" },
  { id: "color", label: "Color" },
  { id: "product", label: "Product" },
  { id: "productVariant", label: "Product Variant" },
  { id: "fabric", label: "Fabric" },
  { id: "purchaseBill", label: "Purchase Bill" },
  { id: "purchaseOrder", label: "Purchase Order" },
  { id: "invoice", label: "Invoice" },
  { id: "entryBook", label: "Entry Book" },
  { id: "platform", label: "Platform" },
  { id: "expenseType", label: "Expense Type" },
  { id: "audit", label: "Audit" },
  { id: "incomeType", label: "Income Type" },
  { id: "sample", label: "Sample" },
];

const Logs = () => {
  const dispatch = useDispatch();

  const logsData = useSelector((state) => state?.logs);
  const supplierData = useSelector((state) => state?.vendor);
  const userData = useSelector((state) => state?.user);

  // Load saved filters from localStorage on component mount
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('logsListFilters');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading saved filters:', error);
      return {};
    }
  };

  const savedFilters = getSavedFilters();

  const [searchText, setSearchText] = useState(savedFilters.searchText || "");
  const [order, setOrder] = useState(savedFilters.order || "desc");
  const [supplierId, setSupplierId] = useState(savedFilters.supplierId || null);
  const [performBy, setPerformBy] = useState(savedFilters.performBy || null);
  const [orderBy, setOrderBy] = useState(savedFilters.orderBy || "createdAt");
  const [moduleName, setModuleName] = useState(savedFilters.moduleName || null);
  const [fromDate, setFromDate] = useState(savedFilters.fromDate || "");
  const [toDate, setToDate] = useState(savedFilters.toDate || "");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "logs" });

  // Save filters to localStorage
  const saveFilters = (filters) => {
    try {
      localStorage.setItem('logsListFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters:', error);
    }
  };

  // Update saved filters when any filter changes
  const updateSavedFilters = (newFilters) => {
    const currentFilters = getSavedFilters();
    const updatedFilters = { ...currentFilters, ...newFilters };
    saveFilters(updatedFilters);
  };

  const getDifferences = (oldData, newData, parentKey = "") => {
    const safeOld = oldData || {};
    const safeNew = newData || {};

    const allKeys = new Set([
      ...Object.keys(safeOld || {}),
      ...Object.keys(safeNew || {}),
    ]);
    let diffs = [];

    allKeys.forEach((key) => {
      if (key === "_id") return; // 👈 skip _id field

      const oldVal = safeOld[key];
      const newVal = safeNew[key];
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (
        typeof oldVal === "object" &&
        oldVal !== null &&
        typeof newVal === "object" &&
        newVal !== null
      ) {
        // Recursive compare for nested objects/arrays
        diffs = diffs.concat(getDifferences(oldVal, newVal, fullKey));
      } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diffs.push({
          field: fullKey,
          oldValue: oldVal ?? "-",
          newValue: newVal ?? "-",
        });
      }
    });

    return diffs;
  };

  const columns = [
    {
      id: "date",
      label: "Date",
      sort: false,
      minWidth: 120,
      render: (row) => moment(row?.date).format("DD-MM-YYYY"),
    },
    {
      id: "moduleName",
      label: "Module",
      sort: false,
    },
    {
      id: "vendor",
      label: "Vendor",
      render: (row) => row?.vendorId?.name ?? "-",
    },
    {
      id: "product",
      label: "Perform by",
      sort: false,
      render: (row) => row?.performBy?.name ?? "-",
    },
    {
      id: "ipAddress",
      label: "IP Address",
    },
    {
      id: "changes",
      label: "Changes",
      render: (row) => {
        const diffs = getDifferences(row?.oldData, row?.newData);
        return diffs.length ? (
          <ul style={{ margin: 0, paddingLeft: "15px" }}>
            {diffs.map((d, i) => (
              <li key={i}>
                <b>{d.field}</b>: {String(d.oldValue)} →{" "}
                <b>{String(d.newValue)}</b>
              </li>
            ))}
          </ul>
        ) : (
          "-"
        );
      },
    },
    { id: "note", label: "Note" },
  ];

  const getData = () => {
    const value = {
      page: page,
      perPage: rowsPerPage,
      search: searchText.trim(),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(supplierId && { vendorId: supplierId?._id }),
      ...(moduleName && { moduleName: moduleName.id }),
      ...(performBy && { performBy: performBy?._id }),
    };
    if (orderBy !== "") {
      value.sort = order === "asc" ? 1 : -1;
      value.field = orderBy;
    }
    dispatch(getLogsList(value));
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleChangePage(0, 0);
      getData();
    }
  };

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
    updateSavedFilters({ order: Order, orderBy: OrderBy });
  };

  // Handle search text change
  const handleSearchChange = (value) => {
    setSearchText(value);
    updateSavedFilters({ searchText: value });
  };

  // Handle date changes
  const handleFromDateChange = (value) => {
    setFromDate(value);
    updateSavedFilters({ fromDate: value });
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    updateSavedFilters({ toDate: value });
  };

  // Handle vendor selection
  const handleSupplierChange = (value) => {
    setSupplierId(value);
    updateSavedFilters({ supplierId: value });
  };

  // Handle perform by selection
  const handlePerformByChange = (value) => {
    setPerformBy(value);
    updateSavedFilters({ performBy: value });
  };

  // Handle module selection
  const handleModuleChange = (value) => {
    setModuleName(value);
    updateSavedFilters({ moduleName: value });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchText("");
    setSupplierId(null);
    setPerformBy(null);
    setModuleName(null);
    localStorage.removeItem('logsListFilters');
    handleChangePage(0, 0);
    getData();
  };

  useEffect(() => {
    dispatch(getVendorList({ sort: 1, field: "name" }));
    dispatch(getUserList({ sort: 1, field: "name" }));
  }, []);

  useEffect(() => {
    getData();
  }, [page, rowsPerPage, dispatch, order, orderBy]);

  return (
    <>
      {logsData?.loading ? (
        <Loader />
      ) : (
        <>
          <Card sx={{ display: "flex", mb: 3 }}>
            <Div sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
              <CardContent>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyPress={handleKeyPress}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              display: searchText === "" ? "none" : "flex",
                            }}
                            onClick={() => handleSearchChange("")}
                          >
                            <ClearIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"From Date"}
                      name="fromDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={fromDate}
                      onChange={(evt) => handleFromDateChange(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="date"
                      label={"To Date"}
                      name="toDate"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                      size="small"
                      value={toDate}
                      onChange={(evt) => handleToDateChange(evt.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <JumboAutoComplete
                      options={supplierData?.list || []}
                      label="Vendor"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`supplierId`}
                      value={supplierId}
                      onChange={(evt, value) => handleSupplierChange(value)}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <JumboAutoComplete
                      options={userData?.list || []}
                      label="Perform By"
                      size="small"
                      getOptionLabel={(option) => option?.name}
                      name={`performBy`}
                      value={performBy}
                      onChange={(evt, value) => handlePerformByChange(value)}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <JumboAutoComplete
                      options={modules}
                      label="Modules"
                      size="small"
                      getOptionLabel={(option) => option?.label}
                      name={`moduleId`}
                      value={moduleName}
                      onChange={(evt, value) => handleModuleChange(value)}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        handleChangePage(0, 0);
                        getData();
                      }}
                    >
                      Submit
                    </Button>
                  </Grid>
                  {/* <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      onClick={clearAllFilters}
                    >
                      Clear Filters
                    </Button>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Div>
          </Card>
          <Card>
            <CardContent>
              <Stack
                justifyContent="space-between"
                direction={{ xs: "column", sm: "row" }}
                sx={{ mb: 3 }}
              >
                <Typography variant={"h2"} mb={0}>
                  Logs List
                </Typography>
              </Stack>
              <Table
                columns={columns}
                rows={logsData?.list}
                page={page}
                rowsPerPage={rowsPerPage}
                Order={order}
                count={logsData?.total}
                OrderBy={orderBy}
                sortData={sortData}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default Logs;
